<template>
    <div>
        <!-- Program Name (below card) -->
        <h2 class="text-xl font-medium text-slate-800 dark:text-slate-100 text-center">
            {{ program.name }}
        </h2>

        <!-- Connected via bank indicator -->
        <div
            v-if="program.connected && program.bank"
            class="flex items-center justify-center gap-2"
        >
            <Icon
                icon="bank-financial"
                :width="17"
                :height="16"
                class="text-slate-400"
            />
            <span class="text-slate-400 dark:text-slate-500 text-sm font-normal leading-5">
                Connected via bank
            </span>
        </div>

        <!-- Action Buttons -->
        <div class="flex justify-center items-center gap-2 pt-4 mb-6">
            <button
                v-if="isManualAccount"
                class="flex items-center gap-2 px-4 py-2 rounded-[4px] border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm dark:shadow-slate-900/50 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors duration-150"
                type="button"
                @click="$emit('update-points')"
            >
                <Icon
                    icon="pencil"
                    :width="20"
                    :height="20"
                    class="text-slate-800 dark:text-slate-300"
                />
                <span class="text-slate-800 dark:text-slate-100 text-sm font-medium leading-5">
                    Update points
                </span>
            </button>
            <button
                v-if="isSyncedAccount"
                class="flex items-center gap-2 px-4 py-2 rounded-[4px] border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm dark:shadow-slate-900/50 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors duration-150"
                type="button"
                @click="$emit('settings')"
            >
                <Icon
                    icon="settings"
                    :width="20"
                    :height="20"
                    class="text-slate-800 dark:text-slate-300"
                />
                <span class="text-slate-800 dark:text-slate-100 text-sm font-medium leading-5">
                    Settings
                </span>
            </button>
            <div class="relative" ref="moreMenuContainer">
                <button
                    class="flex items-center justify-center w-9 h-9 p-2 rounded-[4px] border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm dark:shadow-slate-900/50 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors duration-150"
                    type="button"
                    @click="toggleMoreMenu"
                >
                    <Icon icon="dots-vertical" :width="20" :height="20" />
                </button>
                <!-- Popover Menu -->
                <div
                    v-if="showMoreMenu"
                    class="absolute right-0 mt-2 flex p-2 flex-col items-start border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 shadow-sm dark:shadow-slate-900/50 z-50 min-w-[180px]"
                    @click.stop
                >
                    <!-- Settings (only for connected accounts) -->
                    <button
                        v-if="program?.connected"
                        @click="showMoreMenu = false; $emit('settings')"
                        class="flex w-full px-2 py-1.5 items-center gap-2 rounded hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-800 dark:text-slate-100 transition-colors duration-150"
                    >
                        <Icon icon="settings" :width="20" :height="20" />
                        <span class="text-sm font-normal leading-5">
                            Settings
                        </span>
                    </button>
                    <!-- About Program (only with transfer_partners feature flag) -->
                    <a
                        v-if="canViewAboutProgram"
                        :href="transferPartnersPageUrl"
                        @click="showMoreMenu = false"
                        class="flex w-full px-2 py-1.5 items-center gap-2 rounded hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-800 dark:text-slate-100 transition-colors duration-150 no-underline"
                    >
                        <Icon icon="info" :width="20" :height="20" />
                        <span class="text-sm font-normal leading-5">
                            About program
                        </span>
                    </a>
                    <!-- Delete (only for manual accounts) -->
                    <button
                        v-if="isManualAccount"
                        @click="showMoreMenu = false; $emit('delete-program')"
                        class="flex w-full px-2 py-1.5 items-center gap-2 rounded hover:bg-slate-50 dark:hover:bg-slate-700 text-red-500 transition-colors duration-150"
                    >
                        <Icon icon="delete" :width="20" :height="20" />
                        <span class="text-sm font-normal leading-5">
                            Delete
                        </span>
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue';
import Icon from '@/components/ui/icon/icon.vue';

const props = defineProps({
    program: {
        type: Object,
        required: true,
    },
    isManualAccount: {
        type: Boolean,
        required: true,
    },
    isSyncedAccount: {
        type: Boolean,
        required: true,
    },
    canViewAboutProgram: {
        type: Boolean,
        required: true,
    },
    transferPartnersPageUrl: {
        type: String,
        required: true,
    },
});

defineEmits(['update-points', 'settings', 'delete-program']);

const showMoreMenu = ref(false);
const moreMenuContainer = ref(null);

function toggleMoreMenu() {
    showMoreMenu.value = !showMoreMenu.value;
}

function closeMenuOnOutsideClick(event) {
    if (
        moreMenuContainer.value &&
        !moreMenuContainer.value.contains(event.target)
    ) {
        showMoreMenu.value = false;
    }
}

onMounted(() => {
    document.addEventListener('click', closeMenuOnOutsideClick);
});

onBeforeUnmount(() => {
    document.removeEventListener('click', closeMenuOnOutsideClick);
});
</script>
